/* eslint-disable indent */
/* eslint-disable no-unused-expressions */
/* eslint-disable no-plusplus */
import {
  Box,
  Typography,
  TextField,
  makeStyles,
  Grid,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import React from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import i18n from 'i18n';
import Button from 'components/Button';
import { isValidVerificationCode } from 'utils/tsHelpers';
import { modalClose } from 'modules/modal/actions';
import styles from './styles';

const useStyles = makeStyles(styles);

interface VerificationCodeProps {
  length: number;
  label?: string;
  setVerificationCode: React.Dispatch<React.SetStateAction<string[]>>;
  onRetry?: () => void;
  modalClose: () => void;
  onConfirm: () => void;
}

const VerificationCode: React.FC<VerificationCodeProps> = ({
  length,
  label,
  setVerificationCode,
  onRetry,
  modalClose,
  onConfirm,
}) => {
  const classes = useStyles();
  const [code, setCode] = React.useState<string[]>(Array(length).fill(''));
  const inputs = React.useRef<(HTMLInputElement | null)[]>([]);
  const [countdown, setCountdown] = React.useState(60);
  const [isRetryEnabled, setIsRetryEnabled] = React.useState(false);
  const [restartCountDown, setRestartCountdown] = React.useState(false);
  const [showHelp, setShowHelp] = React.useState(false);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev > 1) return prev - 1;
        clearInterval(timer);
        setIsRetryEnabled(true);
        return 0;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [restartCountDown]);

  const onKeyDown = (e, slot: number) => {
    if (e.key === 'Backspace') {
      e.preventDefault();

      const newCode = [...code];

      if (newCode[slot]) {
        newCode[slot] = '';
        setCode(newCode);

        if (slot > 0 && newCode[slot - 1] === '') {
          inputs.current[slot - 1]?.focus();
        }
      } else if (slot > 0) {
        inputs.current[slot - 1]?.focus();
      }
    }
  };

  const processInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    slot: number
  ) => {
    if (e.target instanceof HTMLInputElement) {
      const input = e.target.value;

      if (/[^0-9]/.test(input)) return;

      const newCode = [...code];
      newCode[slot] = input;
      setCode(newCode);

      if (slot !== length - 1 && input) {
        let nextSlot = slot + 1;

        while (nextSlot < length && newCode[nextSlot] !== '') {
          nextSlot++;
        }

        if (nextSlot < length) {
          inputs.current[nextSlot]?.focus();
        }
      }

      if (newCode.every(num => num !== '')) {
        setVerificationCode(newCode);
      }
    }
  };

  const handlePaste = (e, slot: number) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
    if (pasteData.length > 0) {
      const newCode = [...code];
      const maxLength = Math.min(pasteData.length, length - slot);

      for (let i = 0; i < maxLength; i++) {
        newCode[slot + i] = pasteData[i];
      }

      setCode(newCode);

      const nextSlot =
        slot + maxLength < length ? slot + maxLength : length - 1;
      inputs.current[nextSlot]?.focus();

      if (newCode.every(num => num !== '')) {
        setVerificationCode(newCode);
      }
    }
  };

  const retryMessage = isRetryEnabled
    ? i18n.t('VERIFICATION_CODE_RETRY_KEY')
    : i18n.t(
        countdown === 1
          ? 'VERIFICATION_CODE_RETRY_SECOND_KEY'
          : 'VERIFICATION_CODE_RETRY_SECONDS_KEY',
        { seconds: countdown }
      );

  return (
    <>
      <Grid container>
        <Typography component="div" className={classes.titleDescription}>
          {i18n.t('VERIFICATION_CODE_DESCRIPTION_KEY')}
        </Typography>
      </Grid>
      <Box
        className={classes.codeInput}
        display="flex"
        flexDirection="column"
        alignItems="start"
      >
        <Typography className={classes.codeLabel}>
          {label || i18n.t('VERIFICATION_CODE_KEY')}
        </Typography>
        <Box
          className={classes.codeInputs}
          display="flex"
          justifyContent="start"
          alignItems="center"
        >
          {code.map((num, idx) => (
            <TextField
              key={idx}
              variant="outlined"
              type="text"
              inputMode="numeric"
              inputProps={{
                maxLength: 1,
              }}
              value={num}
              autoFocus={idx === 0 && !code[0]}
              onChange={e => processInput(e, idx)}
              onKeyDown={e => onKeyDown(e, idx)}
              onPaste={e => handlePaste(e, idx)}
              inputRef={ref => {
                inputs.current[idx] = ref;
                return undefined;
              }}
              className={clsx(classes.textField, {
                [classes.textFieldCompleteSuperAdmin]: isValidVerificationCode(
                  code
                ),
              })}
            />
          ))}
        </Box>
      </Box>
      {!!onRetry && (
        <Grid container>
          <Typography component="div" className={classes.retryText}>
            {retryMessage}
            <Box
              display="inline"
              className={clsx({
                [classes.retryTextButton]: isRetryEnabled,
                [classes.retryTextButtonDisabled]: !isRetryEnabled,
              })}
              onClick={() => {
                if (isRetryEnabled) {
                  onRetry();
                  setCountdown(60);
                  setIsRetryEnabled(false);
                  setRestartCountdown(true);
                }
              }}
            >
              {isRetryEnabled ? i18n.t('RESEND_KEY') : i18n.t('WAIT_KEY')}
            </Box>
          </Typography>
        </Grid>
      )}
      <Grid container className={classes.helpButton}>
        <Button
          className={classes.howToOptimize}
          startIcon={<InfoOutlinedIcon className={classes.helpIcon} />}
          onClick={() => setShowHelp(!showHelp)}
        >
          {i18n.t('MORE_INFO_KEY')}
        </Button>
      </Grid>

      {showHelp && (
        <Grid container className={classes.helpGrid}>
          <Typography className={classes.helpDescription}>
            {i18n.t('HELP_VERIFICATION_CODE_ONE_KEY')}
          </Typography>
          <Typography className={classes.helpDescription}>
            {i18n.t('HELP_VERIFICATION_CODE_TWO_KEY')}
          </Typography>
        </Grid>
      )}
      <Grid container className={classes.footerItems}>
        <Button
          id="confirm-button"
          submitButton
          onClick={onConfirm}
          disabled={!isValidVerificationCode(code)}
        >
          {i18n.t('CONFIRM_KEY')}
        </Button>
        <Button id="cancel-button" variant="outlined" onClick={modalClose}>
          {i18n.t('CANCEL_KEY')}
        </Button>
      </Grid>
    </>
  );
};

export default connect(null, { modalClose })(VerificationCode);
